
const navigationStart = Date.now();

export const loadBrowserInfo = async () => {
  const info = {} as any;

  // Helper function to safely execute code and handle exceptions
  const safely = (fn, fallback = null) => {
    try {
      return fn();
    } catch (error) {
      console.warn('Error collecting browser info:', error);
      return fallback;
    }
  };

  // Helper function for async operations
  const safelyAsync = async (fn, fallback = null) => {
    try {
      return await fn();
    } catch (error) {
      console.warn('Error collecting async browser info:', error);
      return fallback;
    }
  };

  // Basic navigator information
  info.userAgent = safely(() => navigator?.userAgent);
  info.platform = safely(() => navigator?.platform);
  info.language = safely(() => navigator?.language);
  info.languages = safely(() => navigator?.languages);
  info.cookieEnabled = safely(() => navigator?.cookieEnabled);

  // Screen information
  info.screen = safely(() => ({
    width: window?.screen?.width,
    height: window?.screen?.height,
    availWidth: window?.screen?.availWidth,
    availHeight: window?.screen?.availHeight,
    colorDepth: window?.screen?.colorDepth,
    pixelDepth: window?.screen?.pixelDepth,
    orientation: window?.screen?.orientation?.type
  }));

  // Viewport information
  info.viewport = safely(() => ({
    width: window?.innerWidth,
    height: window?.innerHeight,
    devicePixelRatio: window?.devicePixelRatio
  }));

  // Hardware information
  info.deviceMemory = safely(() => navigator?.deviceMemory);
  info.hardwareConcurrency = safely(() => navigator?.hardwareConcurrency);

  // Connection information
  info.connection = safely(() => {
    const conn = navigator?.connection || navigator?.mozConnection || navigator?.webkitConnection;
    return conn ? {
      effectiveType: conn.effectiveType,
      downlink: conn.downlink,
      rtt: conn.rtt,
      saveData: conn.saveData
    } : null;
  });

  // URL and route information
  info.url = safely(() => window?.location?.href);
  info.pathname = safely(() => window?.location?.pathname);
  info.search = safely(() => window?.location?.search);
  info.hash = safely(() => window?.location?.hash);
  info.referrer = safely(() => document?.referrer);

  // Timezone information
  info.timezone = safely(() => {
    const options = Intl?.DateTimeFormat?.()?.resolvedOptions?.();
    return {
      timeZone: options?.timeZone,
      locale: options?.locale,
      calendar: options?.calendar,
      numberingSystem: options?.numberingSystem
    };
  });

  // Performance timing
  info.performance = safely(() => {
    const timing = performance?.timing;
    const navigation = performance?.getEntriesByType?.('navigation')?.[0];

    return {
      // Legacy timing API
      timing: timing ? {
        navigationStart: timing.navigationStart,
        loadEventEnd: timing.loadEventEnd,
        domContentLoadedEventEnd: timing.domContentLoadedEventEnd,
        responseEnd: timing.responseEnd,
        requestStart: timing.requestStart
      } : null,

      // Modern Navigation Timing API
      navigation: navigation ? {
        domContentLoadedEventEnd: navigation.domContentLoadedEventEnd,
        loadEventEnd: navigation.loadEventEnd,
        responseEnd: navigation.responseEnd,
        requestStart: navigation.requestStart,
        type: navigation.type
      } : null,

      // Memory info (Chrome only)
      memory: performance?.memory ? {
        usedJSHeapSize: performance.memory.usedJSHeapSize,
        totalJSHeapSize: performance.memory.totalJSHeapSize,
        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit
      } : null
    };
  });

  // Time since page load
  info.timeSincePageLoad = safely(() => {
    return navigationStart ? Date.now() - navigationStart : null;
  });

  // Document visibility
  info.visibilityState = safely(() => document?.visibilityState);
  info.hidden = safely(() => document?.hidden);

  // Storage snapshots
  info.localStorage = safely(() => {
    if (!window?.localStorage) return null;
    const storage = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        // Only include non-sensitive keys or limit data size
        storage[key] = localStorage.getItem(key)?.substring(0, 100); // Truncate for safety
      }
    }
    return storage;
  });

  // Permissions (async)
  info.permissions = await safelyAsync(async () => {
    if (!navigator?.permissions?.query) return null;

    const permissionsToCheck = [
      'geolocation',
      'notifications',
      'camera',
      'microphone',
      'clipboard-read',
      'clipboard-write'
    ];

    const permissions = {};

    await Promise.all(
      permissionsToCheck.map(async (permission) => {
        try {
          const result = await navigator.permissions.query({ name: permission });
          permissions[permission] = result.state;
        } catch (error) {
          permissions[permission] = 'unknown';
        }
      })
    );

    return permissions;
  });

  // Additional browser capabilities
  info.capabilities = safely(() => ({
    webGL: !!window?.WebGLRenderingContext,
    webGL2: !!window?.WebGL2RenderingContext,
    webRTC: !!(navigator?.getUserMedia || navigator?.webkitGetUserMedia || navigator?.mozGetUserMedia || navigator?.mediaDevices?.getUserMedia),
    serviceWorker: 'serviceWorker' in navigator,
    pushManager: 'PushManager' in window,
    indexedDB: 'indexedDB' in window,
    webAssembly: 'WebAssembly' in window,
    intersectionObserver: 'IntersectionObserver' in window,
    mutationObserver: 'MutationObserver' in window
  }));

  // Touch and input capabilities
  info.input = safely(() => ({
    touchSupport: 'ontouchstart' in window || navigator?.maxTouchPoints > 0,
    maxTouchPoints: navigator?.maxTouchPoints,
    pointerEvents: 'PointerEvent' in window,
    hover: window?.matchMedia?.('(hover: hover)')?.matches,
    anyHover: window?.matchMedia?.('(any-hover: hover)')?.matches
  }));

  // Color scheme and media preferences
  info.mediaPreferences = safely(() => ({
    colorScheme: window?.matchMedia?.('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light',
    reducedMotion: window?.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches,
    highContrast: window?.matchMedia?.('(prefers-contrast: high)')?.matches,
    reducedData: window?.matchMedia?.('(prefers-reduced-data: reduce)')?.matches
  }));

  // Battery information (deprecated but might still work in some browsers)
  info.battery = await safelyAsync(async () => {
    if (!navigator?.getBattery) return null;
    const battery = await navigator.getBattery();
    return {
      charging: battery?.charging,
      level: battery?.level,
      chargingTime: battery?.chargingTime,
      dischargingTime: battery?.dischargingTime
    };
  });

  // Timestamp when info was collected
  info.timestamp = new Date().toISOString();

  return info;
};